/**
 * Serialize an unknown error-ish value into a Datadog-compatible `err`
 * block. Shared between circuit-app's Logger and circuit-chat-embed's
 * client-log helper so both frontends emit an identical shape.
 *
 *   - `Error` instances and error-shaped objects → `{ message, name, stack }`
 *     (error-shaped objects also keep extra fields under `details`)
 *   - strings → `{ message: <string> }`
 *   - plain objects → `{ message: <json-stringified> }`
 *   - anything else → `{ message: String(value) }`
 */
import { errorLikeDetails, isErrorLike } from './is-error-like';

export interface FormattedError {
  cause?: FormattedError;
  details?: Record<string, unknown>;
  message: string;
  name?: string;
  stack?: string;
}

export function formatError(error: unknown): FormattedError {
  if (isErrorLike(error)) {
    const result: FormattedError = {
      message: error.message,
      name: error.name,
      stack: error.stack,
    };
    const details = errorLikeDetails(error);
    if (details !== undefined) {
      result.details = details;
    }
    // Recursively format chained cause if present (ES2022 Error.cause).
    const { cause } = error;
    if (cause !== undefined) {
      result.cause = formatError(cause);
    }
    return result;
  }
  if (typeof error === 'string') return { message: error };
  if (error === null || error === undefined) return { message: 'unknown' };
  if (typeof error === 'object') {
    try {
      return { message: JSON.stringify(error) };
    } catch {
      return { message: '[unstringifiable object]' };
    }
  }
  // Narrowed to `bigint | boolean | number | symbol` above (Error / string /
  // null / undefined / object are all handled). The cast is what tells
  // `no-base-to-string` that this call is safe.
  const primitive = error as bigint | boolean | number | symbol;
  return { message: String(primitive) };
}
